import React, { useEffect, useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@material-ui/core';
import { CustomTheme } from 'common/ui/interfaces';
import Button from 'common/components/Button/Button';
import { getStepStyles } from '../styleConstants';
import {
  IInvoicePaymentSplit,
  IPaymentMethodCustomResponse,
  PaymentsType,
} from 'common/interfaces/invoices';
import { formatPrice } from 'common/utils';
import inputLabels from 'common/messages/inputLabels';
import commonMessages from 'common/messages/messages';
import messages from 'common/components/InvoiceOperating/messages';
import errorsMessages from 'common/errors/messages';
import * as actions from 'memberPortalModules/MemberProfile/state/invoice/actions';
import * as selectors from 'memberPortalModules/MemberProfile/state/invoice/selectors';
import { useUpdatePaymentData } from '../useUpdatePaymentData';
import useRootSelector from 'common/hooks/useRootSelector';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import { useLazyGetPaymentCustomMethodQuery } from 'memberPortalModules/MemberProfile/api/pos.api';
import { IServerError } from 'common/interfaces/http';
import BarcodeCard from './BarcodeCard';
import { NumberTextField, TextField } from 'common/components';
import { defaultPriceNumberProps } from 'common/components/NumberController/NumberController';

interface IOtherTypePaymentStepProps {
  leftToPay: number;
  onClose: () => void;
  clubId: number;
}

const useStyles = makeStyles((theme: CustomTheme) => ({
  ...getStepStyles(theme),
  barcodeIcon: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
  },
  barcodeButton: {
    padding: theme.spacing(1),
    margin: theme.spacing(0, 0, 0, -1),

    '& .MuiButton-startIcon': {
      margin: theme.spacing(0, 0.35, 0, 0),
    },
  },
}));

const OtherTypePaymentStep: React.FC<IOtherTypePaymentStepProps> = (
  props: IOtherTypePaymentStepProps,
): JSX.Element => {
  const { leftToPay, onClose, clubId } = props;
  const classes = useStyles(props);
  const [barcodeNumber, setBarcodeNumber] = useState<string>('');
  const [amountToPay, setAmountToPay] = useState<number>(0);
  const dispatch = useDispatch();
  const renderIntlMessage = useRenderIntlMessage();
  const [getPaymentCustomMethod, { isLoading }] = useLazyGetPaymentCustomMethodQuery();

  const [
    paymentMethodPayload,
    setPaymentMethodPayload,
  ] = useState<IPaymentMethodCustomResponse | null>(null);
  const [paymentMethodError, setPaymentMethodError] = useState<IServerError | null>(null);
  const currentInvoice = useRootSelector(selectors.selectMemberInvoice);
  const isUpdatingInvoiceLoading = useRootSelector(selectors.selectUpdatedInvoiceLoading);

  const leftAmount = paymentMethodPayload?.leftAmount || 0;
  const maxAllowedAmount =
    paymentMethodPayload &&
    Number(
      (
        currentInvoice.get('totalAmount') *
        (paymentMethodPayload.paymentMethod.invoiceAmountPercentage / 100)
      ).toFixed(2),
    );

  const maxAllowedAmountByCardleft =
    maxAllowedAmount && leftAmount < maxAllowedAmount ? leftAmount : maxAllowedAmount || 0;

  const isAmountExceedingCardLeft =
    paymentMethodPayload &&
    amountToPay > (paymentMethodPayload?.leftAmount ?? paymentMethodPayload?.amount);

  const isAmountExceedingLeftToPay = amountToPay > leftToPay;

  const isAmountExceedingPMPercentage =
    currentInvoice && paymentMethodPayload && maxAllowedAmount && amountToPay > maxAllowedAmount;

  const isAmountToPayEqualZero = amountToPay === 0;

  const isInvalidAmount =
    isAmountExceedingCardLeft ||
    isAmountExceedingLeftToPay ||
    isAmountExceedingPMPercentage ||
    isAmountToPayEqualZero;

  const canProceed =
    !isLoading &&
    paymentMethodPayload &&
    !paymentMethodError &&
    !isInvalidAmount &&
    !isUpdatingInvoiceLoading &&
    !isAmountToPayEqualZero;

  const handleProceed = () => {
    if (paymentMethodPayload) {
      const invoiceData = currentInvoice.toJS();
      const invoicePaymentSplit: IInvoicePaymentSplit = {
        paymentMethodId: paymentMethodPayload.paymentMethod.id,
        paymentMethodInstanceId: paymentMethodPayload.id,
        type: PaymentsType.CUSTOM,
        paymentAmount: amountToPay,
        giftCardBarcode: barcodeNumber,
      };

      dispatch(actions.updateInvoiceWithSync(invoiceData, invoicePaymentSplit));
    }
  };

  const resetStep = () => {
    setBarcodeNumber('');
    setPaymentMethodPayload(null);
    setAmountToPay(0);
    batch(() => {
      dispatch(actions.resetUpdateInvoiceWithSync());
    });
  };

  useUpdatePaymentData(onClose);

  useEffect(() => {
    return resetStep;
    // Should perform reset only during unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (maxAllowedAmountByCardleft) {
      setAmountToPay(
        leftToPay > maxAllowedAmountByCardleft ? maxAllowedAmountByCardleft : leftToPay,
      );
    }
  }, [maxAllowedAmountByCardleft, leftToPay]);

  const AmountError = () => {
    if (isAmountExceedingCardLeft) {
      return <FormattedMessage {...messages.amountMoreThanLeft} />;
    }

    if (isAmountExceedingLeftToPay) {
      return <FormattedMessage {...messages.amountMoreThanLeftToPay} />;
    }

    if (isAmountExceedingPMPercentage) {
      return (
        <FormattedMessage
          {...messages.amountMoreThanPaymentMethodPercentage}
          values={{
            percentage: paymentMethodPayload.paymentMethod.invoiceAmountPercentage,
            amount: formatPrice(maxAllowedAmountByCardleft),
          }}
        />
      );
    }

    if (isAmountToPayEqualZero) {
      return <FormattedMessage {...errorsMessages.shouldBeMoreThanZero} />;
    }

    return null;
  };

  const handleOnApplyBarcode = () => {
    if (clubId && barcodeNumber) {
      getPaymentCustomMethod({ clubId, barcode: barcodeNumber })
        .unwrap()
        .then(data => {
          setPaymentMethodPayload(data);
          setPaymentMethodError(null);
        })
        .catch(e => {
          setPaymentMethodError(e);
        });
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        {!paymentMethodPayload && (
          <>
            <Grid item xs={12}>
              <TextField
                InputProps={{
                  endAdornment: (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleOnApplyBarcode}
                      disabled={!barcodeNumber || isLoading}
                    >
                      <FormattedMessage {...commonMessages.applyBtn} />
                    </Button>
                  ),
                }}
                variant="outlined"
                label={<FormattedMessage {...inputLabels.barcode} />}
                fullWidth
                defaultValue={barcodeNumber}
                onChange={e => setBarcodeNumber(e.target.value)}
                error={!!paymentMethodError}
                helperText={renderIntlMessage(paymentMethodError?.message)}
              />
            </Grid>
          </>
        )}
        {!!paymentMethodPayload && (
          <>
            <Grid item xs={12}>
              <BarcodeCard barcodePaymentMethod={paymentMethodPayload} onClose={resetStep} />
            </Grid>
            <Grid item xs={12}>
              <NumberTextField
                value={amountToPay}
                onChange={v => setAmountToPay(Number(v))}
                numberFormatProps={defaultPriceNumberProps}
                fullWidth
                variant="outlined"
                label={<FormattedMessage {...inputLabels.amountToPay} />}
                error={isInvalidAmount}
                helperText={<AmountError />}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Grid container spacing={2} className={classes.footerActions}>
        <Grid item sm={12} xs={12}>
          <Button
            fullWidth
            color="primary"
            variant="contained"
            disabled={!canProceed}
            onClick={handleProceed}
          >
            <FormattedMessage
              {...(amountToPay !== leftToPay ? commonMessages.proceedBtn : commonMessages.checkOut)}
            />
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default OtherTypePaymentStep;
